import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import inventoryApi, {
  getAttributeMappings,
  createAttributeMapping,
  updateAttributeMapping,
  deleteAttributeMapping,
} from './inventoryApi';
import api from '../../api';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_META = {
  CONSUMABLE: { cls: 'bg-violet-100 text-violet-700 border border-violet-200' },
  CAPITAL:    { cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
};

const EMPTY_ATTR_FORM = {
  service: '',
  attribute_name: '',
  inventory_item: '',
  quantity_per_use: 1,
};

const EMPTY_FORM = {
  service: '',
  inventory_item: '',
  quantity_per_patient: 1,
};



// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ toasts, removeToast }) => (
  <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => (
      <div
        key={t.id}
        className={`
          pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl text-sm font-medium
          min-w-[280px] max-w-[380px] border backdrop-blur-sm
          ${t.type === 'success' ? 'bg-white border-emerald-200 text-emerald-800' : ''}
          ${t.type === 'error'   ? 'bg-white border-rose-200   text-rose-800'    : ''}
        `}
      >
        {t.type === 'success' && <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500 mt-0.5" />}
        {t.type === 'error'   && <XCircle     className="h-5 w-5 shrink-0 text-rose-500   mt-0.5" />}
        <span className="flex-1 leading-snug">{t.message}</span>
        <button onClick={() => removeToast(t.id)} className="opacity-40 hover:opacity-100 transition-opacity ml-1">
          <X className="h-4 w-4" />
        </button>
      </div>
    ))}
  </div>
);

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

const DeleteDialog = ({ open, mapping, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  const serviceName = mapping?.service_details?.name  || '—';
  const itemName    = mapping?.inventory_item_details?.item_name || '—';

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-rose-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">Delete Mapping</h3>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
              Are you sure you want to delete the mapping for{' '}
              <span className="font-semibold text-slate-700">"{serviceName}"</span>
              {' '}<ArrowRight className="inline h-3.5 w-3.5 text-slate-400" />{' '}
              <span className="font-semibold text-slate-700">"{itemName}"</span>?
              This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};


const ATTRIBUTE_OPTIONS = [
  "height",
  "weight",
  "bp",
  "pulse",
  "oxygen_saturation",
  "body_temperature",
  "inhale",
  "exhale",
  "abdomen",

  "heart_rate",
  "body_fat",
  "visceral_rate",
  "bmr",
  "muscle_mass",
  "muscle_rate",
  "skeletal_muscle",
  "bone_mass",
  "protein_rate",
  "protein_mass",

  "blood_grouping",
  "blood_glucose",
  "ecg_3_lead_1_channel",
  "hemoglobin",

  "acuity",
  "color_blindness",
  "ducochrome",
  "astigmatism",

  "hcv",
  "hbsag",
];


// ─── Attribute Mapping Modal ──────────────────────────────────────────────────

const AttributeMappingModal = ({
  open, mode, formData, onChange, onSubmit, onClose, loading, errors,
  services, inventoryItems, servicesLoading, itemsLoading,
  attributeOptions, attrOptionsLoading,
}) => {
  if (!open) return null;
  const isEdit = mode === 'edit';
  // Dynamic TestParameter codes for the selected service (e.g. ["HB","RBS"]);
  // falls back to hardcoded Vitals list when none exist for this service.
  const attrDropdownOptions = (
    attributeOptions && attributeOptions.length > 0
      ? attributeOptions
      : ATTRIBUTE_OPTIONS
  );

  return (
    <div className="fixed inset-0 z-[998] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {isEdit ? 'Edit Attribute Mapping' : 'Add Attribute Mapping'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Link a service field (e.g. "hbsag") to an inventory item
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-5">

          {/* Service */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Service <span className="text-rose-500">*</span>
            </label>
            {servicesLoading ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <select
                name="service"
                value={formData.service}
                onChange={onChange}
                className={`w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
                  focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                  ${errors.service ? 'border-rose-400' : 'border-slate-200'}`}
              >
                <option value="">Select a service…</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            {errors.service && <p className="mt-1 text-xs text-rose-500">{errors.service}</p>}
          </div>

          {/* Attribute Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Attribute Name <span className="text-rose-500">*</span>
            </label>
            {attrOptionsLoading ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
            <select
  name="attribute_name"
  value={formData.attribute_name}
  onChange={onChange}
  className={`w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
    focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
    ${errors.attribute_name ? 'border-rose-400' : 'border-slate-200'}`}
>
  <option value="">Select Attribute</option>

  {attrDropdownOptions.map((attr) => (
    <option key={attr} value={attr}>
      {attr}
    </option>
  ))}
</select>
            )}
            {attributeOptions && attributeOptions.length > 0 && (
              <p className="mt-1 text-[11px] text-slate-400">
                TestParameter codes for this service — must match code exactly.
              </p>
            )}
          </div>

          {/* Inventory Item */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Inventory Item <span className="text-rose-500">*</span>
            </label>
            {itemsLoading ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <select
                name="inventory_item"
                value={formData.inventory_item}
                onChange={onChange}
                className={`w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
                  focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                  ${errors.inventory_item ? 'border-rose-400' : 'border-slate-200'}`}
              >
                <option value="">Select an item…</option>
                {inventoryItems.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.item_name} ({i.item_code})
                  </option>
                ))}
              </select>
            )}
            {errors.inventory_item && (
              <p className="mt-1 text-xs text-rose-500">{errors.inventory_item}</p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Quantity per Use <span className="text-rose-500">*</span>
            </label>
            <input
              type="number"
              name="quantity_per_use"
              value={formData.quantity_per_use}
              onChange={onChange}
              min={1}
              className={`w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
                focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                ${errors.quantity_per_use ? 'border-rose-400' : 'border-slate-200'}`}
            />
            {errors.quantity_per_use && (
              <p className="mt-1 text-xs text-rose-500">{errors.quantity_per_use}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium rounded-lg text-white flex items-center gap-2 transition-colors"
            style={{ background: loading ? '#9ca3af' : '#11a8a4' }}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Add Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

const MappingModal = ({
  open, mode, formData, onChange, onSubmit, onClose, loading, errors,
  services, inventoryItems, servicesLoading, itemsLoading,
}) => {
  if (!open) return null;

  const isEdit = mode === 'edit';

  return (
    <div className="fixed inset-0 z-[998] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {isEdit ? 'Edit Mapping' : 'Add Mapping'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isEdit
                ? 'Update the service–consumable relationship'
                : 'Link an inventory item to a service for auto-deduction'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Bar */}


        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-5">

          {/* Service */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Service <span className="text-rose-500">*</span>
            </label>
            {servicesLoading ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <select
                name="service"
                value={formData.service}
                onChange={onChange}
                className={`
                  w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
                  focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                  transition-colors
                  ${errors.service ? 'border-rose-400' : 'border-slate-200'}
                `}
              >
                <option value="">Select a service…</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            {errors.service && (
              <p className="mt-1 text-xs text-rose-500">{errors.service}</p>
            )}
          </div>

          {/* Inventory Item */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Inventory Item <span className="text-rose-500">*</span>
            </label>
            {itemsLoading ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <select
                name="inventory_item"
                value={formData.inventory_item}
                onChange={onChange}
                className={`
                  w-full h-10 px-3 rounded-lg border text-sm bg-white text-slate-800
                  focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                  transition-colors
                  ${errors.inventory_item ? 'border-rose-400' : 'border-slate-200'}
                `}
              >
                <option value="">Select an item…</option>
                {inventoryItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.item_name} ({item.item_code || item.category})
                  </option>
                ))}
              </select>
            )}
            {errors.inventory_item && (
              <p className="mt-1 text-xs text-rose-500">{errors.inventory_item}</p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Quantity per Patient <span className="text-rose-500">*</span>
            </label>
            <input
              type="number"
              name="quantity_per_patient"
              min={1}
              value={formData.quantity_per_patient}
              onChange={onChange}
              placeholder="e.g. 2"
              className={`
                w-full h-10 px-3 rounded-lg border text-sm text-slate-800
                focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4]
                transition-colors
                ${errors.quantity_per_patient ? 'border-rose-400' : 'border-slate-200'}
              `}
            />
            {errors.quantity_per_patient && (
              <p className="mt-1 text-xs text-rose-500">{errors.quantity_per_patient}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-60 flex items-center gap-2"
            style={{ backgroundColor: '#11a8a4' }}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Update Mapping' : 'Save Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Skeleton Loader ──────────────────────────────────────────────────────────

const SkeletonRow = ({ cols = 6 }) => (
  <tr className="border-t border-slate-100">
    {[...Array(cols)].map((_, i) => (
      <td key={i} className="px-4 py-3.5">
        <div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${60 + (i * 7) % 30}%` }} />
      </td>
    ))}
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ServiceConsumableMapping = () => {
  // ── data state ──
  const [mappings, setMappings]       = useState([]);
  const [totalPages, setTotalPages]   = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading]         = useState(true);
  // ── Active tab ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('attribute'); // 'attribute' | 'service'

  // ── Attribute mapping state ───────────────────────────────────────────────
  const [attrMappings, setAttrMappings]       = useState([]);
  const [attrLoading, setAttrLoading]         = useState(false);
  const [attrFetchError, setAttrFetchError]   = useState(null);
  const [attrCurrentPage, setAttrCurrentPage] = useState(1);
  const [attrTotalPages, setAttrTotalPages]   = useState(1);
  const [attrSearch, setAttrSearch]           = useState('');

  const [attrModalOpen, setAttrModalOpen]     = useState(false);
  const [attrModalMode, setAttrModalMode]     = useState('add');
  const [attrForm, setAttrForm]               = useState(EMPTY_ATTR_FORM);
  const [attrFormErrors, setAttrFormErrors]   = useState({});
  const [attrSubmitting, setAttrSubmitting]   = useState(false);
  const [attrEditId, setAttrEditId]           = useState(null);

  const [attrDeleteOpen, setAttrDeleteOpen]   = useState(false);
  const [attrDeleteTarget, setAttrDeleteTarget] = useState(null);
  const [attrDeleteLoading, setAttrDeleteLoading] = useState(false);

  // ── Dynamic TestParameter attribute options for selected service ──
  const [testParamAttrs, setTestParamAttrs] = useState([]);
  const [testParamAttrsLoading, setTestParamAttrsLoading] = useState(false);

  useEffect(() => {
    const serviceId = attrForm.service;
    if (!attrModalOpen || !serviceId) {
      setTestParamAttrs([]);
      return;
    }
    let cancelled = false;
    setTestParamAttrsLoading(true);
    api.get("test-parameters/by-service/", { params: { service_id: serviceId } })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const match = rows.find((r) => String(r.service_id) === String(serviceId))
          || rows[0];
        setTestParamAttrs(
          Array.isArray(match?.Attribute) ? match.Attribute : []
        );
      })
      .catch(() => {
        if (!cancelled) setTestParamAttrs([]);
      })
      .finally(() => {
        if (!cancelled) setTestParamAttrsLoading(false);
      });
    return () => { cancelled = true; };
  }, [attrForm.service, attrModalOpen]);

  const [fetchError, setFetchError]   = useState(null);

  // ── dropdown data ──
  const [services, setServices]             = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [itemsLoading, setItemsLoading]       = useState(false);

  // ── search ──
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const debounceTimer                 = useRef(null);

  // ── modal ──
  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState('add'); // 'add' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [formData, setFormData]     = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]         = useState(false);

  // ── delete dialog ──
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // ── toasts ──
  const [toasts, setToasts] = useState([]);

  // ─── Toast helpers ─────────────────────────────────────────────────────────

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ─── Fetch mappings ────────────────────────────────────────────────────────

  const fetchMappings = useCallback(async (page = 1, searchVal = '') => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { page };
      if (searchVal) params.search = searchVal;
      const data = await inventoryApi.getMappings(params);
      // Support both paginated { results, count } and plain array responses
      if (Array.isArray(data)) {
        setMappings(data);
        setTotalPages(1);
      } else {
        setMappings(data.results || []);
        const pageSize = (data.results?.length) || 10;
        setTotalPages(data.count ? Math.ceil(data.count / pageSize) : 1);
      }
    } catch (err) {
      console.error('Error fetching mappings:', err);
      setFetchError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load mappings.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings(currentPage, search);
  }, [fetchMappings, currentPage, search]);



  // ── Attribute mapping fetcher ─────────────────────────────────────────────
  const fetchAttrMappings = useCallback(async (page = 1, q = '') => {
    setAttrLoading(true);
    setAttrFetchError(null);
    try {
      const data = await getAttributeMappings({ page, search: q || undefined });
      setAttrMappings(data.results ?? data);
      if (data.count !== undefined) {
        setAttrTotalPages(Math.ceil(data.count / 10));
      }
    } catch {
      setAttrFetchError('Failed to load attribute mappings.');
    } finally {
      setAttrLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttrMappings(attrCurrentPage, attrSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'attribute') {
      fetchAttrMappings(attrCurrentPage, attrSearch);
    }
  }, [activeTab, attrCurrentPage, attrSearch, fetchAttrMappings]);

  // ── Attribute modal open / close ──────────────────────────────────────────
  const openAttrAddModal = () => {
    fetchDropdownData();
    setAttrForm(EMPTY_ATTR_FORM);
    setAttrFormErrors({});
    setAttrModalMode('add');
    setAttrEditId(null);
    setAttrModalOpen(true);
  };

  const openAttrEditModal = (m) => {
    fetchDropdownData();
    setAttrForm({
      service:          m.service,
      attribute_name:   m.attribute_name,
      inventory_item:   m.inventory_item,
      quantity_per_use: m.quantity_per_use,
    });
    setAttrFormErrors({});
    setAttrModalMode('edit');
    setAttrEditId(m.id);
    setAttrModalOpen(true);
  };

  const closeAttrModal = () => {
    setAttrModalOpen(false);
    setAttrForm(EMPTY_ATTR_FORM);
    setAttrFormErrors({});
  };

  // ── Attribute form change handler ─────────────────────────────────────────
  const handleAttrFormChange = (e) => {
    const { name, value } = e.target;
    setAttrForm((prev) => ({ ...prev, [name]: value }));
    setAttrFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // ── Attribute form validation ─────────────────────────────────────────────
  const validateAttrForm = () => {
    const e = {};
    if (!attrForm.service)        e.service        = 'Service is required.';
    if (!attrForm.attribute_name.trim())
                                  e.attribute_name  = 'Attribute name is required.';
    if (!attrForm.inventory_item) e.inventory_item  = 'Inventory item is required.';
    if (!attrForm.quantity_per_use || Number(attrForm.quantity_per_use) < 1)
                                  e.quantity_per_use = 'Must be at least 1.';
    return e;
  };

  // ── Attribute submit ──────────────────────────────────────────────────────
  const handleAttrSubmit = async () => {
    const e = validateAttrForm();
    if (Object.keys(e).length) { setAttrFormErrors(e); return; }

    setAttrSubmitting(true);
    try {
      const payload = {
        service:          Number(attrForm.service),
        // Trim only — Vitals options are already lowercase, while
        // TestParameter codes (e.g. "HB") must keep exact case to match backend.
        attribute_name:   attrForm.attribute_name.trim(),
        inventory_item:   Number(attrForm.inventory_item),
        quantity_per_use: Number(attrForm.quantity_per_use),
      };

      if (attrModalMode === 'edit') {
        await updateAttributeMapping(attrEditId, payload);
        addToast('Attribute mapping updated.', 'success');
      } else {
        await createAttributeMapping(payload);
        addToast('Attribute mapping created.', 'success');
      }

      closeAttrModal();
      fetchAttrMappings(attrCurrentPage, attrSearch);
    } catch (err) {
      const msg =
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        'Failed to save. Please try again.';
      addToast(msg, 'error');
    } finally {
      setAttrSubmitting(false);
    }
  };

  // ── Attribute delete ──────────────────────────────────────────────────────
  const openAttrDeleteDialog = (m) => {
    setAttrDeleteTarget(m);
    setAttrDeleteOpen(true);
  };

  const handleAttrDelete = async () => {
    if (!attrDeleteTarget) return;
    setAttrDeleteLoading(true);
    try {
      await deleteAttributeMapping(attrDeleteTarget.id);
      addToast('Attribute mapping deleted.', 'success');
      setAttrDeleteOpen(false);
      setAttrDeleteTarget(null);
      fetchAttrMappings(attrCurrentPage, attrSearch);
    } catch {
      addToast('Failed to delete. Please try again.', 'error');
    } finally {
      setAttrDeleteLoading(false);
    }
  };

  // ─── Fetch dropdown data ───────────────────────────────────────────────────

  const fetchDropdownData = useCallback(async () => {
    setServicesLoading(true);
    setItemsLoading(true);
    try {
      const [svcRes, itemsRes] = await Promise.all([
        api.get('/services/'),
        inventoryApi.getItems({ is_active: true, page_size: 999 }),
      ]);

      // Services
      const svcData = svcRes.data;
      setServices(Array.isArray(svcData) ? svcData : (svcData.results || []));

      // Inventory items — use the result from Promise.all, not a second call
      const itemsData = itemsRes;
      setInventoryItems(Array.isArray(itemsData) ? itemsData : (itemsData.results || []));
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    } finally {
      setServicesLoading(false);
      setItemsLoading(false);
    }
  }, []);

  // ─── Search debounce ───────────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1);
    }, 300);
  };

  // ─── Modal helpers ─────────────────────────────────────────────────────────

  const openAddModal = () => {
    fetchDropdownData();
    setModalMode('add');
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (mapping) => {
    fetchDropdownData();
    setModalMode('edit');
    setEditTarget(mapping);
    setFormData({
      service:              mapping.service_details?.id   ?? mapping.service,
      inventory_item:       mapping.inventory_item_details?.id ?? mapping.inventory_item,
      quantity_per_patient: mapping.quantity_per_patient,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.service)        errs.service        = 'Please select a service.';
    if (!formData.inventory_item) errs.inventory_item = 'Please select an inventory item.';
    if (!formData.quantity_per_patient || Number(formData.quantity_per_patient) < 1)
      errs.quantity_per_patient = 'Quantity must be at least 1.';
    return errs;
  };


  const handleSubmit = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const payload = {
      service:              Number(formData.service),
      inventory_item:       Number(formData.inventory_item),
      quantity_per_patient: Number(formData.quantity_per_patient),
    };

    setSaving(true);
    try {
      if (modalMode === 'edit') {
        await inventoryApi.updateMapping(editTarget.id, payload);
        addToast('Mapping updated successfully.', 'success');
      } else {
        await inventoryApi.createMapping(payload);
        addToast('Mapping created successfully.', 'success');
      }
      setModalOpen(false);
      fetchMappings(currentPage, search);
    } catch (err) {
      console.error('Error saving mapping:', err);
      const msg =
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to save mapping.';
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete helpers ────────────────────────────────────────────────────────

  const openDeleteDialog = (mapping) => {
    setDeleteTarget(mapping);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await inventoryApi.deleteMapping(deleteTarget.id);
      addToast('Mapping deleted.', 'success');
      setDeleteOpen(false);
      setDeleteTarget(null);
      // If deleting last item on page, go back one page
      const newPage = mappings.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      fetchMappings(newPage, search);
    } catch (err) {
      console.error('Error deleting mapping:', err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to delete mapping.';
      addToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Toasts */}
      <Toast toasts={toasts} removeToast={removeToast} />

      {/* Modals */}
      <MappingModal
        open={modalOpen}
        mode={modalMode}
        formData={formData}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        onClose={closeModal}
        loading={saving}
        errors={formErrors}
        services={services}
        inventoryItems={inventoryItems}
        servicesLoading={servicesLoading}
        itemsLoading={itemsLoading}
      />

      <DeleteDialog
        open={deleteOpen}
        mapping={deleteTarget}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) { setDeleteOpen(false); setDeleteTarget(null); } }}
        loading={deleting}
      />

      


      {/* ── Attribute Mapping Modals ─────────────────────────────────── */}
      <AttributeMappingModal
        open={attrModalOpen}
        mode={attrModalMode}
        formData={attrForm}
        onChange={handleAttrFormChange}
        onSubmit={handleAttrSubmit}
        onClose={closeAttrModal}
        loading={attrSubmitting}
        errors={attrFormErrors}
        services={services}
        inventoryItems={inventoryItems}
        servicesLoading={servicesLoading}
        itemsLoading={itemsLoading}
        attributeOptions={testParamAttrs}
        attrOptionsLoading={testParamAttrsLoading}
      />

      <DeleteDialog
        open={attrDeleteOpen}
        mapping={attrDeleteTarget}
        onConfirm={handleAttrDelete}
        onCancel={() => { setAttrDeleteOpen(false); setAttrDeleteTarget(null); }}
        loading={attrDeleteLoading}
      />

      {/* ── Page Content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Service Consumable Mapping
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Map service attributes to inventory items for automatic deduction when a patient is served.
            </p>
          </div>
          {activeTab === 'attribute' && (
            <button
              onClick={openAttrAddModal}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:opacity-90 active:scale-95 transition-all"
              style={{ backgroundColor: '#11a8a4' }}
            >
              <Plus className="h-4 w-4" />
              Add Attribute Mapping
            </button>
          )}
          {activeTab === 'service' && (
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:opacity-90 active:scale-95 transition-all"
              style={{ backgroundColor: '#94a3b8' }}
            >
              <Plus className="h-4 w-4" />
              Add Legacy Mapping
            </button>
          )}
        </div>



        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6">
          <button
            onClick={() => setActiveTab('attribute')}
            className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'attribute'
                ? 'border-[#11a8a4] text-[#11a8a4]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Attribute Mappings
          </button>

          <button
            onClick={() => setActiveTab('service')}
            className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'service'
                ? 'border-slate-400 text-slate-600'
                : 'border-transparent text-slate-400 hover:text-slate-500'
            }`}
          >
            Service Mappings
            <span className="ml-2 text-xs font-semibold bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">
              Legacy
            </span>
          </button>
        </div>


        {/* ── SERVICE MAPPING TAB ─────────────────────────────────────── */}
        {activeTab === 'service' && (
          <div>
                    {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

          {/* Search bar */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search by service or item name…"
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 focus:border-[#11a8a4] transition-colors"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Service Name', 'Inventory Item', 'Category', 'Unit', 'Qty / Patient', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* Loading skeleton */}
                {loading && [...Array(6)].map((_, i) => <SkeletonRow key={i} />)}

                {/* Error state */}
                {!loading && fetchError && (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <XCircle className="h-10 w-10 text-rose-300" />
                        <p className="text-sm text-slate-500">{fetchError}</p>
                        <button
                          onClick={() => fetchMappings(currentPage, search)}
                          className="mt-1 text-sm font-medium px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Empty state */}
                {!loading && !fetchError && mappings.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-1">
                          <Search className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-600">No mappings found</p>
                        <p className="text-xs text-slate-400">
                          {search ? 'Try a different search term.' : 'Click "+ Add Mapping" to create one.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {!loading && !fetchError && mappings.map((m) => {
                  const item     = m.inventory_item_details || {};
                  const service  = m.service_details        || {};
                  const catKey   = (item.category || '').toUpperCase();
                  const catMeta  = CATEGORY_META[catKey];

                  return (
                    <tr
                      key={m.id}
                      className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors group"
                    >
                      {/* Service */}
                      <td className="px-4 py-3.5">
                        <span className="font-medium text-slate-800">{service.name || '—'}</span>
                      </td>

                      {/* Item */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800">{item.item_name || '—'}</span>
                          {item.item_code && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200 font-mono tracking-wide">
                              {item.item_code}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3.5">
                        {catMeta ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${catMeta.cls}`}>
                            {catKey}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>

                      {/* Unit */}
                      <td className="px-4 py-3.5 text-slate-500">
                        {item.unit || '—'}
                      </td>

                      {/* Qty */}
                      <td className="px-4 py-3.5">
                        <span className="text-xl font-bold text-slate-800">
                          {m.quantity_per_patient ?? '—'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openEditModal(m)}
                            title="Edit"
                            className="p-1.5 rounded-lg text-slate-500 hover:text-[#11a8a4] hover:bg-[#11a8a4]/10 transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteDialog(m)}
                            title="Delete"
                            className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && !fetchError && totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-100 bg-slate-50">
              <span className="text-xs text-slate-500">
                Page <span className="font-semibold text-slate-700">{currentPage}</span> of{' '}
                <span className="font-semibold text-slate-700">{totalPages}</span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

        </div>
          </div>
        )}

        {/* ── ATTRIBUTE MAPPING TAB ───────────────────────────────────── */}
        {activeTab === 'attribute' && (
          <div>

            {/* Toolbar */}
            <div className="flex items-center px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Attribute Mappings</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Map individual form fields (e.g. "hbsag") to inventory items
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={attrSearch}
                  onChange={(e) => { setAttrSearch(e.target.value); setAttrCurrentPage(1); }}
                  placeholder="Search by service or attribute name…"
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm text-slate-800
                    placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40
                    focus:border-[#11a8a4] transition-colors"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {['Service', 'Attribute Field', 'Inventory Item', 'Category', 'Unit', 'Qty / Use', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Loading */}
                  {attrLoading && [...Array(5)].map((_, i) => <SkeletonRow key={i} cols={7} />)}

                  {/* Error */}
                  {!attrLoading && attrFetchError && (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center">
                        <p className="text-sm text-slate-500">{attrFetchError}</p>
                        <button
                          onClick={() => fetchAttrMappings(attrCurrentPage, attrSearch)}
                          className="mt-2 text-sm font-medium px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* Empty */}
                  {!attrLoading && !attrFetchError && attrMappings.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-1">
                            <Search className="h-5 w-5 text-slate-400" />
                          </div>
                          <p className="text-sm font-medium text-slate-600">No attribute mappings found</p>
                          <p className="text-xs text-slate-400">
                            {attrSearch ? 'Try a different search term.' : 'Click "+ Add Attribute Mapping" to create one.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data rows */}
                  {!attrLoading && !attrFetchError && attrMappings.map((m) => {
                    const item    = m.inventory_item_details || {};
                    const service = m.service_details        || {};
                    const catKey  = (item.category || '').toUpperCase();
                    const catMeta = CATEGORY_META[catKey];
                    return (
                      <tr
                        key={m.id}
                        className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors group"
                      >
                        {/* Service */}
                        <td className="px-4 py-3.5">
                          <span className="font-medium text-slate-800">{service.name || '—'}</span>
                        </td>

                        {/* Attribute */}
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-xs font-mono font-semibold">
                            {m.attribute_name}
                          </span>
                        </td>

                        {/* Item */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800">{item.item_name || '—'}</span>
                            {item.item_code && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200 font-mono">
                                {item.item_code}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3.5">
                          {catMeta ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${catMeta.cls}`}>
                              {catKey}
                            </span>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </td>

                        {/* Unit */}
                        <td className="px-4 py-3.5 text-slate-500">{item.unit || '—'}</td>

                        {/* Qty */}
                        <td className="px-4 py-3.5">
                          <span className="text-xl font-bold text-slate-800">{m.quantity_per_use ?? '—'}</span>
                        </td>

                        {/* Active badge */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                            {!m.is_active && (
                              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mr-1">
                                Inactive
                              </span>
                            )}
                            <button
                              onClick={() => openAttrEditModal(m)}
                              title="Edit"
                              className="p-1.5 rounded-lg text-slate-500 hover:text-[#11a8a4] hover:bg-[#11a8a4]/10 transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openAttrDeleteDialog(m)}
                              title="Delete"
                              className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!attrLoading && !attrFetchError && attrTotalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-100 bg-slate-50">
                <span className="text-xs text-slate-500">
                  Page <span className="font-semibold text-slate-700">{attrCurrentPage}</span> of{' '}
                  <span className="font-semibold text-slate-700">{attrTotalPages}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAttrCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={attrCurrentPage === 1}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Previous
                  </button>
                  <button
                    onClick={() => setAttrCurrentPage((p) => Math.min(attrTotalPages, p + 1))}
                    disabled={attrCurrentPage === attrTotalPages}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}




      </div>
    </div>
  );
};

export default ServiceConsumableMapping;